import React from 'react';
import RiyalSymbol from './RiyalSymbol';
import { BoothConfig, HourlyPackage, TicketSystemConfig } from '../services/ConfigService';

interface BoothPricingEditorProps {
  booth: BoothConfig;
  onUpdate: (customPricing: any) => void;
}

const DEFAULT_HOURLY_PACKAGES: HourlyPackage[] = [
  { hours: 2, price: 1200, enabled: true },
  { hours: 3, price: 1500, enabled: true },
  { hours: 4, price: 1800, enabled: true }
];

const DEFAULT_TICKET_SYSTEM: TicketSystemConfig = {
  installationFee: 2500,
  freeTickets: 15,
  ticketPrice: 35,
  enabled: true
};

export const BoothPricingEditor: React.FC<BoothPricingEditorProps> = ({ booth, onUpdate }) => {
  // Fix: Mongoose might return an empty array for hourlyPackages, so we must check length
  const customPricing = booth.customPricing || { enabled: false };
  const packagesToRender = customPricing.hourlyPackages?.length ? customPricing.hourlyPackages : DEFAULT_HOURLY_PACKAGES;
  const ticketSystemToRender = customPricing.ticketSystem || DEFAULT_TICKET_SYSTEM;
  
  const isEnabled = customPricing.enabled;

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({
      ...customPricing,
      enabled: e.target.checked,
      // Initialize with defaults if empty
      hourlyPackages: packagesToRender,
      ticketSystem: ticketSystemToRender
    });
  };

  const handleHourlyUpdate = (index: number, updates: Partial<HourlyPackage>) => {
    const newPackages = [...packagesToRender];
    newPackages[index] = { ...newPackages[index], ...updates };
    onUpdate({ ...customPricing, hourlyPackages: newPackages, ticketSystem: ticketSystemToRender });
  };

  const handleTicketUpdate = (updates: Partial<TicketSystemConfig>) => {
    const newSystem = { ...ticketSystemToRender, ...updates };
    onUpdate({ ...customPricing, ticketSystem: newSystem, hourlyPackages: packagesToRender });
  };

  return (
    <div className="custom-pricing-section" style={{ marginTop: '30px', borderTop: '2px dashed var(--border-color, #eaeaea)', paddingTop: '20px', color: 'var(--text-color, #333)' }}>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h4 style={{ margin: 0, color: 'var(--text-color, #333)' }}>
          التسعير المخصص
        </h4>
        <label className="switch">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={handleToggle}
          />
          <span className="slider"></span>
        </label>
      </div>

      {isEnabled && (
        <div className="pricing-group" style={{ padding: '20px', borderRadius: '8px', marginTop: '15px', border: '1px solid var(--border-color, #eee)', backgroundColor: 'var(--bg-color, #f9f9f9)' }}>
          <p style={{ fontSize: '14px', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-color, #555)' }}>
            <span>💡</span> عندما يتم تفعيل التسعير المخصص، هذه الكابينة ستتجاهل الأسعار العامة وستستخدم هذه الأسعار فقط.
          </p>

          <div className="pricing-group" style={{ marginBottom: '30px' }}>
            <h5 style={{ borderBottom: '1px solid var(--border-color, #ddd)', paddingBottom: '10px', marginBottom: '15px', color: 'var(--text-color, #333)' }}>باقات الساعات الخاصة بالكابينة</h5>
            {packagesToRender.map((pkg, index) => (
              <div key={index} className="package-config" style={{ padding: '15px', border: '1px solid var(--border-color, #eee)', borderRadius: '8px', marginBottom: '15px', backgroundColor: 'var(--card-bg, #fff)' }}>
                <div className="config-header">
                  <h6 style={{ margin: 0, color: 'var(--text-color, #333)' }}>باقة {pkg.hours} ساعات</h6>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={pkg.enabled}
                      onChange={(e) => handleHourlyUpdate(index, { enabled: e.target.checked })}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="config-fields horizontal" style={{ marginTop: '10px' }}>
                  <div className="field-group">
                    <label>عدد الساعات</label>
                    <input
                      type="number"
                      value={pkg.hours}
                      onChange={(e) => handleHourlyUpdate(index, { hours: parseInt(e.target.value) || 0 })}
                      min="1"
                    />
                  </div>
                  <div className="field-group">
                    <label>السعر <RiyalSymbol size={16} /></label>
                    <input
                      type="number"
                      value={pkg.price}
                      onChange={(e) => handleHourlyUpdate(index, { price: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="pricing-group">
            <h5 style={{ borderBottom: '1px solid var(--border-color, #ddd)', paddingBottom: '10px', marginBottom: '15px', color: 'var(--text-color, #333)' }}>نظام التذاكر الخاص بالكابينة</h5>
            <div className="package-config" style={{ padding: '15px', border: '1px solid var(--border-color, #eee)', borderRadius: '8px', backgroundColor: 'var(--card-bg, #fff)' }}>
              <div className="config-fields">
                <div className="field-group">
                  <label>رسوم التركيب <RiyalSymbol size={16} /></label>
                  <input
                    type="number"
                    value={ticketSystemToRender.installationFee}
                    onChange={(e) => handleTicketUpdate({ installationFee: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
                <div className="field-group">
                  <label>التذاكر المجانية</label>
                  <input
                    type="number"
                    value={ticketSystemToRender.freeTickets}
                    onChange={(e) => handleTicketUpdate({ freeTickets: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
                <div className="field-group">
                  <label>سعر التذكرة الإضافية <RiyalSymbol size={16} /></label>
                  <input
                    type="number"
                    value={ticketSystemToRender.ticketPrice}
                    onChange={(e) => handleTicketUpdate({ ticketPrice: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
